import React, { useEffect, useState, useCallback, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import MacBar from "@/components/MacBar";
import Logo from "@/components/Logo";
import Orb from "@/components/Orb";
import WakeListener from "@/components/WakeListener";
import VoiceOverlay from "@/components/VoiceOverlay";
import ForcePasswordModal from "@/components/ForcePasswordModal";
import OnboardingWizard from "@/components/OnboardingWizard";
import { api, streamMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AppLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const voiceConvRef = useRef(null);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Start a fresh voice session (one conversation kept for the whole back-and-forth).
  const openVoice = useCallback(() => {
    voiceConvRef.current = null;
    setVoiceOpen(true);
  }, []);

  // Wake event opens the overlay globally
  useEffect(() => {
    const onWake = () => openVoice();
    window.addEventListener("disciple:wake", onWake);
    return () => window.removeEventListener("disciple:wake", onWake);
  }, [openVoice]);

  // Tell wake listener to pause while overlay is open
  useEffect(() => {
    window.dispatchEvent(new Event(voiceOpen ? "disciple:voice-open" : "disciple:voice-close"));
  }, [voiceOpen]);

  // A voice turn: post to the session's conversation (reused for context) and
  // return the assistant's reply text for the overlay to speak.
  const onVoiceSubmit = useCallback(async (text) => {
    if (!text.trim()) return "";
    let cid = voiceConvRef.current;
    if (!cid) {
      const conv = await api.post("/conversations");
      cid = conv.data.id;
      voiceConvRef.current = cid;
      navigate(`/chat/${cid}`);
    }
    let full = "";
    let content = "";
    for await (const ev of streamMessage(cid, text)) {
      if (ev.type === "delta") full += ev.data.t;
      else if (ev.type === "done") content = ev.data.message?.content || full;
      else if (ev.type === "error") throw new Error("stream error");
    }
    // Let the visible chat refresh if it happens to be showing this conversation.
    window.dispatchEvent(new CustomEvent("disciple:conv-updated", { detail: { cid } }));
    return (content || full).replace(/```action[\s\S]*?```/g, "").replace(/\n{2,}/g, " ").trim();
  }, [navigate]);

  return (
    <div className="app-shell">
      <MacBar />
      <div className="mobile-topbar" data-testid="mobile-topbar">
        <button
          className="btn btn-ghost"
          style={{ padding: 8 }}
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu size={20} strokeWidth={2.2} />
        </button>
        <Logo height={24} />
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Sidebar mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
        {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} data-testid="sidebar-backdrop" />}
        <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--void)" }}>
          <Outlet />
        </main>
      </div>
      <WakeListener />
      {!voiceOpen && (
        <button
          className="floating-orb"
          onClick={openVoice}
          title={`Talk to ${user?.assistant_name || "Andre"}`}
          aria-label={`Talk to ${user?.assistant_name || "Andre"}`}
          data-testid="floating-orb-btn"
        >
          <Orb size={50} state="listening" />
          <span className="floating-orb-label">Talk to {user?.assistant_name || "Andre"}</span>
        </button>
      )}
      <VoiceOverlay
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSubmit={onVoiceSubmit}
        assistantName={user?.assistant_name || "Andre"}
      />
      {user?.must_change_password && <ForcePasswordModal />}
      {!user?.must_change_password && user?.onboarded === false && <OnboardingWizard />}
    </div>
  );
}
